import { type ComponentType } from 'react';
import { Link, Outlet, useLocation, useNavigate } from 'react-router';
import { useTranslation } from 'react-i18next';
import { Building2, ChartColumn, DoorOpen, FileText, House, LogOut, MessagesSquare, User, Wallet } from 'lucide-react';
import { Menu, type ItemMenu } from '../../../web/src/componentes/menu';
import { nomeEmpresa, useSessao } from '../sessao';
import { FaixaAmbiente, Marca } from './comum';
import { PularParaConteudo } from '../../../web/src/componentes/pular-conteudo';
import { telaDaRota } from '../ajuda';
import { useModoSimples } from '../modo-simples';
import { BotaoAjuda, PainelAjuda, useAjuda } from './Ajuda';

interface ItemNav {
  chave: 'hoje' | 'saida' | 'financeiro' | 'relatorios' | 'documentos' | 'mensagens';
  rota: string;
  icone: ComponentType<{ className?: string; 'aria-hidden'?: boolean }>;
}

/**
 * Itens da navegação: só a seção que é real aparece (financeiro sem o módulo ou sem a área some; mensagens sem o Conversas;
 * E1: relatórios com o Contas e o Relatórios do escritório e a área financeira do acesso).
 */
// U15 F5: no modo simples o menu não mostra Relatórios (a rota segue aberta pelo link).
export function itensNav(secoes: { financeiro: boolean; mensagens: boolean; relatorios?: boolean }, saida = false, simples = false): ItemNav[] {
  return [
    // Trajetórias (item 5): no modo saída, o início é a tela do acesso de saída (sem Hoje, relatórios nem mensagens).
    saida ? { chave: 'saida' as const, rota: '/', icone: DoorOpen } : { chave: 'hoje' as const, rota: '/', icone: House },
    ...(secoes.financeiro ? [{ chave: 'financeiro' as const, rota: '/financeiro', icone: Wallet }] : []),
    ...(secoes.relatorios && !simples ? [{ chave: 'relatorios' as const, rota: '/relatorios', icone: ChartColumn }] : []),
    { chave: 'documentos', rota: '/documentos', icone: FileText },
    ...(secoes.mensagens ? [{ chave: 'mensagens' as const, rota: '/mensagens', icone: MessagesSquare }] : []),
  ];
}

const ativo = (rota: string, caminho: string) => (rota === '/' ? caminho === '/' : caminho === rota || caminho.startsWith(`${rota}/`));

/** Faixa vermelha do Sandbox (mesmo token da faixa de ambiente do MalgoOS) e a faixa da visualização da equipe. */
function Faixas() {
  const { t } = useTranslation('portal');
  const s = useSessao();
  return <FaixaAmbiente avisos={[...(s.sandbox ? [t('faixa.sandbox')] : []), ...(s.somente_leitura ? [t('faixa.verComo')] : [])]} />;
}

function MenuConta({ aoSair }: { aoSair: () => void }) {
  const { t } = useTranslation('portal');
  const s = useSessao();
  const navegar = useNavigate();
  const simples = useModoSimples();
  const itens: ItemMenu[] = [
    { chave: 'perfil', rotulo: t('menu.perfil'), icone: <User className="size-4" aria-hidden />, aoClicar: () => navegar('/perfil') },
    // U15 F5: a troca de empresas some do menu da conta no modo simples (a rota /empresas segue aberta pelo link).
    ...(!s.somente_leitura && !simples ? [{ chave: 'empresas', rotulo: t('menu.empresas'), icone: <Building2 className="size-4" aria-hidden />, aoClicar: () => navegar('/empresas') }] : []),
    { chave: 'sair', rotulo: t('menu.sair'), icone: <LogOut className="size-4" aria-hidden />, aoClicar: aoSair },
  ];
  const nome = s.conta?.nome || s.conta?.email || nomeEmpresa(s);
  return (
    <Menu
      rotulo={t('menu.conta')}
      itens={itens}
      classePainel="right-0 w-56"
      classeGatilho="flex size-9 items-center justify-center rounded-full bg-sup-3 text-sm font-semibold text-tinta-1 hover:bg-sup-2"
      gatilho={<span aria-hidden>{nome.trim().slice(0, 1).toUpperCase()}</span>}
    />
  );
}

/**
 * O esqueleto do app: no celular, cabeçalho com a empresa e a barra inferior (Hoje, Financeiro, Documentos, Mensagens),
 * com as margens seguras do aparelho (notch e barra de gestos); no tablet e no computador (iPad, Mac), barra lateral.
 */
export function Shell({ aoSair }: { aoSair: () => void }) {
  const { t } = useTranslation('portal');
  const s = useSessao();
  const local = useLocation();
  const simples = useModoSimples();
  const itens = itensNav(s.secoes, !!s.saida, simples);
  // U15 F1 e F2: a ajuda da tela atual (Hoje, Financeiro, Mensagens, Documentos, Perfil). Na visualização da equipe (só leitura) não abre sozinha.
  const tela = telaDaRota(local.pathname, !!s.saida);
  const ajuda = useAjuda(tela, !s.somente_leitura);
  return (
    <div className="min-h-dvh bg-fundo">
      <PularParaConteudo />
      <Faixas />
      <div className="flex min-h-dvh">
        {/* Lote 19 design 2: menu lateral na superfície da marca (Strong Green com texto Ice Gray), como o menu do MalgoOS e o topo
            do portal do ERP antigo; os tokens são redefinidos só aqui dentro (`.superficie-marca`, tokens.css). */}
        <aside className="superficie-marca topo-faixa altura-sob-faixa sticky hidden w-64 shrink-0 border-r border-borda bg-sup-1 pt-seguro md:block">
          <nav aria-label={t('menu.principal')} className="flex h-full flex-col gap-4 p-4">
            <Marca />
            <div className="rounded-lg border border-borda bg-sup-2 px-3 py-2">
              <p className="truncate text-sm font-semibold" title={nomeEmpresa(s)}>
                {nomeEmpresa(s)}
              </p>
              <p className="truncate text-xs text-tinta-3" title={s.empresa.escritorio}>
                {s.empresa.escritorio}
              </p>
            </div>
            <ul className="flex flex-1 flex-col gap-0.5">
              {itens.map((i) => (
                <li key={i.chave}>
                  <Link
                    to={i.rota}
                    aria-current={ativo(i.rota, local.pathname) ? 'page' : undefined}
                    data-nav={i.chave}
                    className={`flex items-center gap-3 rounded-lg border-l-3 px-3 py-2 text-sm transition-colors ${ativo(i.rota, local.pathname) ? 'border-acento-forte bg-sup-3 font-semibold text-tinta-1' : 'border-transparent font-medium text-tinta-2 hover:bg-sup-2 hover:text-tinta-1'}`}
                  >
                    <i.icone className="size-4 shrink-0" aria-hidden />
                    {t(`menu.${i.chave}`)}
                  </Link>
                </li>
              ))}
            </ul>
            {tela && <BotaoAjuda className="w-full justify-start" aoClicar={ajuda.abrir} />}
            <div className="flex items-center gap-2 border-t border-borda pt-3">
              <Link to="/perfil" className="flex min-w-0 flex-1 items-center gap-2 rounded-md hover:text-acento-forte">
                <span className="flex size-8 shrink-0 items-center justify-center rounded-full bg-sup-3 text-xs font-semibold" aria-hidden>
                  {(s.conta?.nome || s.conta?.email || nomeEmpresa(s)).slice(0, 1).toUpperCase()}
                </span>
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-sm font-medium">{s.conta?.nome || t('menu.perfil')}</span>
                  <span className="block truncate text-xs text-tinta-3">{t(`papel.${s.papel}`)}</span>
                </span>
              </Link>
              <button type="button" onClick={aoSair} className="flex items-center gap-1.5 rounded-md px-2 py-1.5 text-xs text-tinta-3 hover:bg-sup-2 hover:text-tinta-1">
                <LogOut className="size-4" aria-hidden />
                <span>{t('menu.sair')}</span>
              </button>
            </div>
          </nav>
        </aside>
        <div className="flex min-w-0 flex-1 flex-col">
          {/* Lote 19 design 2: topo do celular na superfície da marca, como o topo (chrome) do portal do ERP antigo. */}
          <header className="superficie-marca topo-faixa sticky z-20 flex items-center gap-3 border-b border-borda bg-fundo px-4 pt-seguro-3 pb-3 md:hidden">
            <Marca />
            <div className="min-w-0 flex-1 text-right">
              <p className="truncate text-sm font-semibold" title={nomeEmpresa(s)}>
                {nomeEmpresa(s)}
              </p>
            </div>
            {tela && <BotaoAjuda aoClicar={ajuda.abrir} />}
            <MenuConta aoSair={aoSair} />
          </header>
          <main id="conteudo" tabIndex={-1} className="mx-auto w-full max-w-5xl flex-1 px-4 pt-4 pb-seguro-nav focus:outline-none md:px-8 md:pt-8 md:pb-10">
            <Outlet />
          </main>
          <nav aria-label={t('menu.principal')} className="fixed inset-x-0 bottom-0 z-30 border-t border-borda bg-sup-1 pb-seguro md:hidden">
            <ul className="mx-auto flex max-w-md">
              {itens.map((i) => (
                <li key={i.chave} className="flex-1">
                  <Link
                    to={i.rota}
                    data-nav={i.chave}
                    aria-current={ativo(i.rota, local.pathname) ? 'page' : undefined}
                    className={`flex flex-col items-center gap-1 py-2 text-xs font-medium ${ativo(i.rota, local.pathname) ? 'text-acento-forte' : 'text-tinta-2'}`}
                  >
                    <i.icone className="size-5" aria-hidden />
                    {t(`menu.${i.chave}`)}
                  </Link>
                </li>
              ))}
            </ul>
          </nav>
        </div>
      </div>
      {tela && <PainelAjuda tela={tela} aberto={ajuda.aberto} aoFechar={ajuda.fechar} />}
    </div>
  );
}
